import type { UsuarioWorkflow } from './models';

export interface TravaConfig {
  limiteDividaTecnica: number;
  percentualAlerta: number;
  hardStopAtivo: boolean;
  failSafeAtivo: boolean;
  evidenciaObrigatoria: boolean;
}

export interface TravaResposta extends TravaConfig {
  dataUltimaAlteracao: string | null;
  dividaTecnicaAtual: number;
  percentualUtilizado: number;
  nivelAlerta: 'NORMAL' | 'ALERTA' | 'BLOQUEIO';
}

export interface OverrideTemporario {
  overrideId: number;
  empresaId: number;
  nomeEmpresa: string;
  motivo: string;
  validadeHoras: number;
  criadoEm: string;
  expiraEm: string;
  criadoPorNome: string;
  status: 'ATIVO' | 'EXPIRADO' | 'REVOGADO';
}

export const NIVEL_MINIMO_TRAVA = 7;
export const VALIDADES_OVERRIDE = [1, 2, 4, 8, 24, 48, 72];

/** Governança de TI é Nível 7: Administrador de TI ou N7 podem alterar políticas e exceções (o backend valida igual). */
export const podeAlterarTrava = (perfil?: Pick<UsuarioWorkflow, 'adminTi' | 'nivel'> | null): boolean =>
  !!perfil && (!!perfil.adminTi || (perfil.nivel ?? 0) >= NIVEL_MINIMO_TRAVA);

export const moeda = (v: number): string => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

export const rotuloValidade = (horas: number): string => (horas % 24 === 0 && horas >= 24 ? `${horas / 24} dia(s)` : `${horas} hora(s)`);

/** Texto do limite digitado ("1.500.000,50" / "1500000.5") → número; vazio/ inválido → NaN. */
export const lerValorMonetario = (texto: string): number => {
  const t = texto.trim().replace(/[R$\s]/g, '');
  if (!t) return NaN;
  // "1.500.000" (só pontos de milhar, padrão pt-BR) e "1.500.000,50" são lidos como no Brasil; "1500000.5" como decimal simples.
  const milharPtBr = /^\d{1,3}(\.\d{3})+$/.test(t);
  const normal = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : milharPtBr ? t.replace(/\./g, '') : t;
  return Number(normal);
};

export const validarTrava = (limiteTexto: string, percentual: number, justificativa: string): string | null => {
  const limite = lerValorMonetario(limiteTexto);
  if (!Number.isFinite(limite) || limite <= 0) return 'Informe um limite de dívida técnica maior que zero.';
  if (!Number.isInteger(percentual) || percentual < 1 || percentual > 100) return 'O percentual de alerta deve estar entre 1 e 100.';
  if (!justificativa.trim()) return 'Informe a justificativa SOX.';
  return null;
};

const onOff = (v: boolean) => (v ? 'ativo' : 'desativado');

/** Justificativa SOX sugerida: descreve exatamente o que mudou nas políticas (a pessoa pode editar). */
export const justificativaTrava = (antes: TravaConfig | null, depois: TravaConfig): string => {
  if (!antes) return 'Definição inicial das políticas de trava de segurança do Workflow SOX.';
  const mudancas: string[] = [];
  if (antes.limiteDividaTecnica !== depois.limiteDividaTecnica)
    mudancas.push(`limite de dívida técnica de ${moeda(antes.limiteDividaTecnica)} para ${moeda(depois.limiteDividaTecnica)}`);
  if (antes.percentualAlerta !== depois.percentualAlerta)
    mudancas.push(`alerta em ${antes.percentualAlerta}% → ${depois.percentualAlerta}% do limite`);
  if (antes.hardStopAtivo !== depois.hardStopAtivo) mudancas.push(`Hard Stop sistêmico ${onOff(depois.hardStopAtivo)}`);
  if (antes.failSafeAtivo !== depois.failSafeAtivo) mudancas.push(`Fail-Safe ${onOff(depois.failSafeAtivo)}`);
  if (antes.evidenciaObrigatoria !== depois.evidenciaObrigatoria) mudancas.push(`evidência obrigatória ${onOff(depois.evidenciaObrigatoria)}`);
  return mudancas.length === 0
    ? 'Revisão das políticas de trava de segurança, sem alterações.'
    : `Ajuste das políticas de trava de segurança: ${mudancas.join('; ')}.`;
};

export const justificativaOverride = (empresa?: string, horas?: number): string =>
  `Liberação excepcional${empresa ? ` para a empresa "${empresa}"` : ''}${horas ? ` por ${rotuloValidade(horas)}` : ''}, ` +
  'autorizada pela Governança de TI para viabilizar o fechamento, com expiração automática e registro SOX.';

export const classeNivelAlerta = (n: TravaResposta['nivelAlerta']): string =>
  n === 'BLOQUEIO' ? 'danger' : n === 'ALERTA' ? 'warning' : 'success';

export const rotuloNivelAlerta = (n: TravaResposta['nivelAlerta'], percentualAlerta: number): string =>
  n === 'BLOQUEIO' ? 'Limite atingido — novas conciliações bloqueadas (Hard Stop)'
    : n === 'ALERTA' ? `Acima de ${percentualAlerta}% do limite — atenção`
      : 'Dentro do limite';
